<!--
 * @Author: Tahiti
 * @Date: 2025-12-17
 * @Description: 特效卡片组件（鼠标移入显示 Use This Template 按钮）
 * @FilePath: /weryai-web/components/Effects/EffectsCard.vue
-->
<template>
    <div
        class="effects-card relative overflow-hidden cursor-pointer group"
        :style="cardStyle"
        @click="handleClick"
        @mouseenter="playPreview"
        @mouseleave="pausePreview"
    >
        <!-- 视频：鼠标移入时尝试播放 -->
        <video
            v-if="item.video"
            ref="videoRef"
            class="absolute inset-0 size-full object-cover"
            :poster="posterSrc"
            :src="item.video"
            loop
            muted
            playsinline
            webkit-playsinline
            preload="none"
            @loadedmetadata="onMediaLoad"
            @loadeddata="handleVideoFrameReady"
            @canplay="handleVideoCanPlay"
            @canplaythrough="handleVideoCanPlay"
            @playing="handleVideoPlaying"
            @pause="handleVideoPause"
            @waiting="handleVideoWaiting"
            :style="{ opacity: showVideoThumbnail ? 0 : 1 }"
        ></video>
        <img
            v-if="showVideoThumbnail"
            :src="posterSrc"
            :alt="item.title"
            loading="lazy"
            decoding="async"
            width="400"
            height="225"
            class="absolute inset-0 size-full object-cover"
            @load="handlePosterLoaded"
            @error="handlePosterError"
        />
        <!-- 兜底：如果没有视频，只展示封面图 -->
        <img
            v-else-if="item.cover"
            :src="item.cover"
            :alt="item.title"
            loading="lazy"
            decoding="async"
            width="400"
            height="225"
            class="absolute inset-0 size-full object-cover"
            @load="handleImageLoad"
        />
        <EffectsSkeletonCard
            v-if="shouldShowMediaSkeleton"
            variant="video"
            overlay
        />

        <!-- 热度/点赞数 -->
        <div
            v-if="item.likes && !shouldShowMediaSkeleton"
            class="absolute top-3 left-3 z-[2] flex items-center justify-center gap-1 text-white text-xs rounded-md"
            style="width: 58px; height: 32px; background: rgba(0, 0, 0, 0.6)"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
            >
                <path
                    d="M12.1108 4.03674C10.2908 4.58491 9.95763 6.13877 10.075 7.12569C8.77669 5.51397 8.83 3.65991 8.83 0.98877C4.66605 2.64689 5.63443 7.42763 5.5099 8.8794C4.46255 7.97408 4.26465 5.81096 4.26465 5.81096C3.15902 6.41179 2.60461 8.01638 2.60461 9.31794C2.60461 12.4654 5.02022 15.0168 7.99997 15.0168C10.9797 15.0168 13.3953 12.4654 13.3953 9.31794C13.3953 7.44747 12.0951 6.58464 12.1108 4.03674Z"
                    fill="white"
                />
            </svg>
            {{ formatNumber(item.likes) }}
        </div>

        <!-- PC 视频状态图标 -->
        <div
            v-if="
                item.video &&
                !shouldShowMediaSkeleton &&
                (showPlayIcon || showLoadingIcon)
            "
            class="hidden lg:flex absolute top-3 left-3 z-[2] size-5 items-center justify-center transition-opacity duration-200"
        >
            <svg
                v-if="showPlayIcon"
                width="20"
                height="20"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <circle cx="18" cy="18" r="18" fill="black" fill-opacity="0.35" />
                <path
                    d="M16.1824 10.9008C14.6834 9.97494 12.75 11.0532 12.75 12.8151V23.186C12.75 24.9479 14.6833 26.0262 16.1824 25.1004L24.5779 19.9149C26.0014 19.0356 26.0014 16.9655 24.5779 16.0863L16.1824 10.9008Z"
                    fill="white"
                />
            </svg>

            <svg
                v-else
                class="preview-loading-spinner"
                width="20"
                height="20"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <circle cx="18" cy="18" r="18" fill="black" fill-opacity="0.35" />
                <path
                    d="M11.5348 12.5599C11.5348 13.1971 11.7879 13.8082 12.2385 14.2588C12.689 14.7093 13.3001 14.9624 13.9373 14.9624C14.5745 14.9624 15.1856 14.7093 15.6362 14.2588C16.0867 13.8082 16.3399 13.1971 16.3399 12.5599C16.3399 12.2444 16.2777 11.932 16.157 11.6405C16.0362 11.349 15.8593 11.0841 15.6362 10.861C15.4131 10.6379 15.1482 10.461 14.8567 10.3402C14.5652 10.2195 14.2528 10.1573 13.9373 10.1573C13.6218 10.1573 13.3094 10.2195 13.0179 10.3402C12.7264 10.461 12.4615 10.6379 12.2385 10.861C12.0154 11.0841 11.8384 11.349 11.7176 11.6405C11.5969 11.932 11.5348 12.2444 11.5348 12.5599ZM9.73341 17.6647C9.73261 17.9412 9.78639 18.2152 9.89166 18.4709C9.99693 18.7267 10.1516 18.9591 10.3469 19.1549C10.5422 19.3507 10.7742 19.5061 11.0296 19.6121C11.285 19.7181 11.5588 19.7727 11.8354 19.7727C12.1119 19.7727 12.3857 19.7181 12.6411 19.6121C12.8966 19.5061 13.1285 19.3507 13.3238 19.1549C13.5191 18.9591 13.6738 18.7267 13.7791 18.4709C13.8843 18.2152 13.9381 17.9412 13.9373 17.6647C13.9381 17.3881 13.8843 17.1142 13.7791 16.8584C13.6738 16.6027 13.5191 16.3703 13.3238 16.1744C13.1285 15.9786 12.8966 15.8232 12.6411 15.7172C12.3857 15.6112 12.1119 15.5566 11.8354 15.5566C11.5588 15.5566 11.285 15.6112 11.0296 15.7172C10.7742 15.8232 10.5422 15.9786 10.3469 16.1744C10.1516 16.3703 9.99693 16.6027 9.89166 16.8584C9.78639 17.1142 9.73261 17.3881 9.73341 17.6647ZM11.5352 22.1692C11.5352 22.6471 11.725 23.1053 12.0629 23.4432C12.4008 23.7812 12.8591 23.971 13.337 23.971C13.8149 23.971 14.2732 23.7812 14.6111 23.4432C14.949 23.1053 15.1388 22.6471 15.1388 22.1692C15.1388 21.6913 14.949 21.233 14.6111 20.8951C14.2732 20.5572 13.8149 20.3674 13.337 20.3674C12.8591 20.3674 12.4008 20.5572 12.0629 20.8951C11.725 21.233 11.5352 21.6913 11.5352 22.1692ZM16.6499 24.1514C16.6499 24.5691 16.8158 24.9698 17.1112 25.2651C17.4066 25.5605 17.8072 25.7264 18.2249 25.7264C18.6426 25.7264 19.0432 25.5605 19.3386 25.2651C19.634 24.9698 19.7999 24.5691 19.7999 24.1514C19.7999 23.7337 19.634 23.3331 19.3386 23.0377C19.0432 22.7424 18.6426 22.5764 18.2249 22.5764C17.8072 22.5764 17.4066 22.7424 17.1112 23.0377C16.8158 23.3331 16.6499 23.7337 16.6499 24.1514ZM21.7448 22.4698C21.7448 22.8681 21.903 23.25 22.1846 23.5316C22.4662 23.8133 22.8482 23.9715 23.2465 23.9715C23.6447 23.9715 24.0267 23.8133 24.3083 23.5316C24.5899 23.25 24.7481 22.8681 24.7481 22.4698C24.7481 22.0715 24.5899 21.6896 24.3083 21.408C24.0267 21.1264 23.6447 20.9681 23.2465 20.9681C22.8482 20.9681 22.4662 21.1264 22.1846 21.408C21.903 21.6896 21.7448 22.0715 21.7448 22.4698ZM24.1478 17.3645C24.1478 17.6831 24.2743 17.9886 24.4996 18.2138C24.7248 18.439 25.0303 18.5656 25.3489 18.5656C25.6674 18.5656 25.9729 18.439 26.1981 18.2138C26.4234 17.9886 26.5499 17.6831 26.5499 17.3645C26.5499 17.046 26.4234 16.7405 26.1981 16.5153C25.9729 16.29 25.6674 16.1635 25.3489 16.1635C25.0303 16.1635 24.7248 16.29 24.4996 16.5153C24.2743 16.7405 24.1478 17.046 24.1478 17.3645ZM22.3456 12.86C22.3456 12.9783 22.3689 13.0955 22.4142 13.2048C22.4594 13.3141 22.5258 13.4134 22.6094 13.4971C22.6931 13.5807 22.7924 13.6471 22.9017 13.6924C23.011 13.7376 23.1282 13.7609 23.2465 13.7609C23.3648 13.7609 23.4819 13.7376 23.5912 13.6924C23.7005 13.6471 23.7998 13.5807 23.8835 13.4971C23.9672 13.4134 24.0335 13.3141 24.0788 13.2048C24.1241 13.0955 24.1474 12.9783 24.1474 12.86C24.1474 12.6211 24.0524 12.392 23.8835 12.223C23.7145 12.054 23.4854 11.9591 23.2465 11.9591C23.0075 11.9591 22.7784 12.054 22.6094 12.223C22.4405 12.392 22.3456 12.6211 22.3456 12.86ZM17.9999 10.5011C17.9999 10.58 18.0154 10.6582 18.0456 10.731C18.0758 10.8039 18.1201 10.8702 18.1759 10.9259C18.2316 10.9817 18.2979 11.026 18.3708 11.0562C18.4436 11.0864 18.5218 11.1019 18.6007 11.1019C18.6796 11.1019 18.7577 11.0864 18.8306 11.0562C18.9034 11.026 18.9697 10.9817 19.0255 10.9259C19.0812 10.8702 19.1255 10.8039 19.1557 10.731C19.1859 10.6582 19.2014 10.58 19.2014 10.5011C19.2014 10.4222 19.1859 10.3441 19.1557 10.2712C19.1255 10.1984 19.0812 10.1321 19.0255 10.0763C18.9697 10.0206 18.9034 9.97631 18.8306 9.94612C18.7577 9.91593 18.6796 9.90039 18.6007 9.90039C18.5218 9.90039 18.4436 9.91593 18.3708 9.94612C18.2979 9.97631 18.2316 10.0206 18.1759 10.0763C18.1201 10.1321 18.0758 10.1984 18.0456 10.2712C18.0154 10.3441 17.9999 10.4222 17.9999 10.5011Z"
                    fill="white"
                />
            </svg>
        </div>

        <!-- PC hover 遮罩：位于媒体上方、文案和按钮下方 -->
        <div
            v-if="!shouldShowMediaSkeleton"
            class="hidden lg:block absolute inset-0 z-[1] pointer-events-none opacity-0 transition-opacity duration-300 lg:group-hover:opacity-100"
            style="background: rgba(0, 0, 0, 0.1)"
        ></div>

        <!-- 底部渐变蒙层 -->
        <div
            v-if="!shouldShowMediaSkeleton"
            class="absolute bottom-0 left-0 right-0 h-20 z-[1] pointer-events-none"
            style="
                background: linear-gradient(
                    to top,
                    rgba(0, 0, 0, 0.7),
                    transparent
                );
            "
        ></div>

        <!-- 底部标题：默认显示，PC 端 hover 时隐藏 -->
        <p
            v-if="item.title && showTitle && !shouldShowMediaSkeleton"
            class="absolute bottom-4 left-1/2 -translate-x-1/2 z-[2] text-white text-sm font-medium truncate max-w-[90%] text-center opacity-100 lg:group-hover:opacity-0 transition-opacity duration-300"
        >
            {{ item.title }}
        </p>

        <!-- Use This Template 按钮：移动端隐藏，PC 端 hover 时显示 -->
        <button
            v-if="!shouldShowMediaSkeleton"
            class="hidden lg:block absolute bottom-4 left-1/2 -translate-x-1/2 z-[3] py-2 px-6 bg-[#99F768] text-[#000] text-sm font-normal leading-normal rounded-lg lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
            @click.stop="handleUseTemplate"
        >
            {{ t('menus.useThisTemplate') }}
        </button>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { getResourceThumbnail } from '~/utils/common'

interface EffectItem {
    id: string | number
    video?: string
    cover?: string
    title?: string
    likes?: number
}

const props = withDefaults(
    defineProps<{
        item: EffectItem
        width?: number
        height?: number
        showTitle?: boolean
        autoSize?: boolean // 自适应容器尺寸（移动端使用）
        showMediaSkeleton?: boolean
    }>(),
    {
        width: 240,
        height: 320,
        showTitle: true,
        autoSize: false,
        showMediaSkeleton: false,
    }
)

const emit = defineEmits<{
    click: [item: EffectItem]
    useTemplate: [item: EffectItem]
    mediaLoad: [item: EffectItem, actualWidth: number, actualHeight: number]
}>()

const { t } = useI18n()

const videoRef = ref<HTMLVideoElement | null>(null)
const isHovering = ref(false)
const isPreviewPlaying = ref(false)
const isPreviewLoading = ref(false)
const isPreviewReady = ref(false)
const isMediaReady = ref(
    !props.showMediaSkeleton || (!props.item.video && !props.item.cover)
)
const isPosterLoaded = ref(false)
const isPosterError = ref(false)

// poster 使用 video 的 thumbnail 或 cover 的 thumbnail
const posterSrc = computed(() => {
    if (props?.item?.video) {
        return getResourceThumbnail(props.item.video)
    }
    return props?.item?.cover ? getResourceThumbnail(props.item.cover) : ''
})

const shouldShowMediaSkeleton = computed(
    () => {
        if (!props.showMediaSkeleton) return false
        if (props.item.video) {
            return !isMediaReady.value && (!posterSrc.value || (!isPosterLoaded.value && !isPosterError.value))
        }
        return !!props.item.cover && !isMediaReady.value
    }
)

const showVideoThumbnail = computed(
    () =>
        !!props.item.video &&
        !!posterSrc.value &&
        !isMediaReady.value &&
        !isPosterError.value
)

const emitMediaLoad = (actualWidth: number, actualHeight: number) => {
    if (actualWidth > 0 && actualHeight > 0) {
        emit('mediaLoad', props.item, actualWidth, actualHeight)
    }
}

// 动态样式
const cardStyle = computed(() => {
    if (props.autoSize) {
        return {
            width: '100%',
            height: '100%',
        }
    }
    return {
        width: `${props.width}px`,
        height: `${props.height}px`,
    }
})

// 格式化数字
const formatNumber = (num: number): string => {
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
}

const showLoadingIcon = computed(
    () =>
        !!props.item.video &&
        isHovering.value &&
        isPreviewLoading.value &&
        !isPreviewReady.value &&
        !isPreviewPlaying.value
)

const showPlayIcon = computed(
    () => !!props.item.video && !isHovering.value && !isPreviewPlaying.value
)

const syncPreviewReadyState = () => {
    const video = videoRef.value
    isPreviewReady.value = !!video && video.readyState >= 3
    if (isPreviewReady.value) {
        isPreviewLoading.value = false
    }
}

// 视频播放控制（绑定在卡片容器上，避免 hover 按钮/蒙层导致 video mouseleave 抖动）
const warmUpOnce = (video: HTMLVideoElement) => {
    if ((video.dataset as any).weryWarm === '1') return
    ;(video.dataset as any).weryWarm = '1'
    try {
        // 只提升 preload，不主动调用 load()：
        // load() 可能导致媒体管线重置（poster/首帧闪一下）并触发额外 206 请求。
        video.preload = 'auto'
    } catch {
        // ignore
    }
}

let pauseTimer: ReturnType<typeof setTimeout> | null = null

const playPreview = async () => {
    isHovering.value = true

    const video = videoRef.value
    if (!video) return

    if (pauseTimer) {
        clearTimeout(pauseTimer)
        pauseTimer = null
    }

    warmUpOnce(video)

    if (!video.paused && !video.ended) {
        syncPreviewReadyState()
        isPreviewPlaying.value = true
        isPreviewLoading.value = false
        return
    }

    syncPreviewReadyState()
    isPreviewPlaying.value = false
    isPreviewLoading.value = !isPreviewReady.value

    try {
        await video.play()
    } catch (error: any) {
        syncPreviewReadyState()
        isPreviewPlaying.value = false
        isPreviewLoading.value = isHovering.value && !isPreviewReady.value
        if (error?.name !== 'AbortError') {
            console.warn('视频播放失败:', error)
        }
    }
}

const pausePreview = () => {
    isHovering.value = false
    isPreviewPlaying.value = false
    isPreviewLoading.value = false
    isPreviewReady.value = false

    const video = videoRef.value
    if (!video) return
    // hover 快速抖动时不要立刻 pause（会 Abort 网络/解码，导致下一次 hover 又要等）
    if (pauseTimer) clearTimeout(pauseTimer)
    pauseTimer = setTimeout(() => {
        pauseTimer = null
        if (videoRef.value && !videoRef.value.paused) {
            videoRef.value.pause()
        }
    }, 250)
}

const handleVideoPlaying = () => {
    if (!isHovering.value) return
    syncPreviewReadyState()
    isPreviewPlaying.value = true
    isPreviewLoading.value = false
}

const handleVideoPause = () => {
    syncPreviewReadyState()
    isPreviewPlaying.value = false
    isPreviewLoading.value = isHovering.value && !isPreviewReady.value
}

const handleVideoWaiting = () => {
    if (!isHovering.value) return
    syncPreviewReadyState()
    isPreviewPlaying.value = false
    isPreviewLoading.value = !isPreviewReady.value
}

const handleVideoCanPlay = () => {
    syncPreviewReadyState()
    if (videoRef.value?.readyState && videoRef.value.readyState >= 2) {
        isMediaReady.value = true
    }
}

// 媒体加载完成，获取实际尺寸
const onMediaLoad = (event: Event) => {
    const target = event.target as HTMLImageElement | HTMLVideoElement
    if (!target) return

    const actualWidth =
        (target as HTMLVideoElement)?.videoWidth ||
        (target as HTMLImageElement).naturalWidth ||
        0
    const actualHeight =
        (target as HTMLVideoElement)?.videoHeight ||
        (target as HTMLImageElement).naturalHeight ||
        0

    emitMediaLoad(actualWidth, actualHeight)
}

const handleVideoFrameReady = (event: Event) => {
    isMediaReady.value = true
    onMediaLoad(event)
}

const handlePosterLoaded = (event: Event) => {
    isPosterLoaded.value = true
    isPosterError.value = false
    const target = event.target as HTMLImageElement | null
    emitMediaLoad(target?.naturalWidth || 0, target?.naturalHeight || 0)
}

const handlePosterError = () => {
    isPosterLoaded.value = false
    isPosterError.value = true
}

const handleImageLoad = (event: Event) => {
    isMediaReady.value = true
    onMediaLoad(event)
}

watch(
    () => [props.item.id, props.item.video, props.item.cover, posterSrc.value, props.showMediaSkeleton],
    () => {
        isHovering.value = false
        isPreviewPlaying.value = false
        isPreviewLoading.value = false
        isPreviewReady.value = false
        isMediaReady.value =
            !props.showMediaSkeleton || (!props.item.video && !props.item.cover)
        isPosterLoaded.value = false
        isPosterError.value = false
    }
)

onBeforeUnmount(() => {
    if (pauseTimer) {
        clearTimeout(pauseTimer)
        pauseTimer = null
    }
})

// 点击卡片
const handleClick = () => {
    emit('click', props.item)
}

// 点击使用模板
const handleUseTemplate = () => {
    emit('useTemplate', props.item)
}
</script>

<style lang="less" scoped>
.effects-card {
    background: #1b1d25;
}

.preview-loading-spinner {
    animation: preview-spin 1s linear infinite;
}

@keyframes preview-spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
